import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Loads data with loading and error state and a reload function.
 * `loader` should be stable (wrap in useCallback). Ignores results after unmount.
 */
export default function useAsync(loader, { immediate = true } = {}) {
  const [state, setState] = useState({ data: null, loading: immediate, error: null });
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const reload = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await loader();
      if (alive.current) setState({ data, loading: false, error: null });
      return data;
    } catch (error) {
      if (alive.current) setState((s) => ({ data: silent ? s.data : null, loading: false, error }));
      return null;
    }
  }, [loader]);

  useEffect(() => {
    if (immediate) reload();
  }, [immediate, reload]);

  return { ...state, reload };
}
